// What this deal bills, clause by clause, beside what each clause is worth.
//
// A term line is a RULE and a statement line is what that rule came to. The two
// belong on one row, because the question a person actually asks of a deal is
// "does this clause say what we agreed, and is the figure it produced the one I
// expected" — and answering it used to mean reading a revenue line here, a
// schedule item there and a usage actual on a third screen.
//
// THE CLAUSES READ AS A SENTENCE, not as JSON. `termLineSentence` turns the
// five clauses into one line of prose; the expression behind it is still there
// to be edited, and the sentence is what a person checks the contract against.
//
// THE PERIOD PICKER DRIVES THE FIGURE. A statement is one period's
// interpretation, so a section that showed "the figure" without saying which
// month would be showing a number nobody can check. Stepping the month re-reads
// the statement and nothing else.
//
// THE LIST IS THE CLAUSES IN FORCE, and the rest sit behind one row. A deal
// accumulates clauses: a month of expenses, one month of AI usage, a retainer
// nobody bills any more. Drawn together, each with "does not bill" against it,
// the table stops being a series of line items and becomes an archive. So a
// clause the chosen period does not cover is counted at the foot instead, where
// a person can still open it.

import { useState, type ReactNode } from "react";
import type { DealListItem, DealStatementLine, TermLine } from "@crm/shared";
import styles from "./TermLinesSection.module.css";
import { savedTermLineSentence } from "./termLineSentence.js";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { Notice, Spinner } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { useDealStatement, useTermLines } from "../../api/queries.js";
import { formatCalendarDate } from "../../format/calendarDate.js";
import { formatMonth } from "../../format/calendarDate.js";
import { formatMoney } from "../../format/money.js";
import { noteSummary } from "../../format/noteSummary.js";
import { termLineKindLabel } from "../../format/labels.js";
import { StatementPanel } from "./StatementPanel.js";

export interface TermLinesSectionProps {
  deal: DealListItem;
  /** The month the statement is read for. Owned by the deal page. */
  period: string;
  onPeriodChange: (period: string) => void;
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (line: TermLine) => void;
  onDelete: (line: TermLine) => void;
  /** A line the form is trying out, drawn beside the saved ones. */
  preview: DealStatementLine | null;
  /** Opens the fact dialog for a statement line with no figure. */
  onRecordFact: (line: DealStatementLine) => void;
}

export function TermLinesSection({
  deal,
  period,
  onPeriodChange,
  canEdit,
  onAdd,
  onEdit,
  onDelete,
  preview,
  onRecordFact,
}: TermLinesSectionProps): ReactNode {
  const lines = useTermLines(deal.id);
  const statement = useDealStatement(deal.id, period);
  const [showOutOfForce, setShowOutOfForce] = useState(false);

  const answer = statement.data;
  // A 202 means the usage rollup does not cover this period and a job is
  // filling it. The figures are not wrong — they are not there yet — so the
  // section says so rather than drawing an empty column.
  const ready = answer !== undefined && answer.kind === "ready" ? answer.statement : null;
  const figures = new Map<string, DealStatementLine>();
  for (const line of ready?.lines ?? []) figures.set(line.term_line_id, line);

  return (
    <>
      <Card>
        <CardHeader
          title="Term lines"
          description="Each one is a clause of this deal's interpretation: which facts it reads, how many units they carry, what a unit is worth, whose money that is, and which invoice it lands on."
          actions={
            <div className={styles.headerActions}>
              <PeriodStepper period={period} onChange={onPeriodChange} />
              {canEdit && (
                <Button variant="primary" small onClick={onAdd}>
                  <span aria-hidden="true">+</span>
                  Add a term line
                </Button>
              )}
            </div>
          }
        />

        {answer !== undefined && answer.kind === "pending" && (
          <Notice tone="info" title="This period's figures are still being worked out.">
            <Spinner inline label="Filling the rollup" />
            {" The clauses below are current; their figures appear when the job finishes."}
          </Notice>
        )}

        <QueryBoundary query={lines} label="this deal's term lines">
          {(page) => {
            const covering = page.items.filter((line) => inForce(line, period));
            const parked = page.items.filter((line) => !inForce(line, period));
            const shown = readingOrder(showOutOfForce ? [...covering, ...parked] : covering);
            const columns = canEdit ? 4 : 3;
            return (
              <Table label={`Term lines for ${deal.customer_name}`}>
                <thead>
                  <tr>
                    <Th>Clause</Th>
                    <Th>In force</Th>
                    <Th numeric>{formatMonth(period)}</Th>
                    {canEdit && <Th />}
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <EmptyRow
                      colSpan={columns}
                      title={
                        page.items.length === 0
                          ? "No term lines"
                          : `No clause is in force in ${formatMonth(period)}`
                      }
                      description={
                        page.items.length === 0
                          ? "This deal interprets no facts yet, so every period it covers is worth nothing. Add the clause the paper states."
                          : "Every clause this deal carries has a window that ends before this month or starts after it. Step the month, or open the clauses below."
                      }
                    />
                  ) : (
                    shown.map((line) => (
                      <TermLineRow
                        key={line.id}
                        line={line}
                        parked={!inForce(line, period)}
                        figure={figures.get(line.id)}
                        canEdit={canEdit}
                        onEdit={() => onEdit(line)}
                        onDelete={() => onDelete(line)}
                      />
                    ))
                  )}
                  {parked.length > 0 && (
                    <tr>
                      <td colSpan={columns} className={styles.parked}>
                        <button
                          type="button"
                          className={styles.parkedToggle}
                          onClick={() => setShowOutOfForce(!showOutOfForce)}
                        >
                          {`${String(parked.length)} clause${
                            parked.length === 1 ? "" : "s"
                          } not in force in ${formatMonth(period)} — ${
                            showOutOfForce ? "hide" : "show"
                          }`}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            );
          }}
        </QueryBoundary>
      </Card>

      {ready !== null && (
        <StatementPanel
          statement={ready}
          onRecordFact={canEdit ? onRecordFact : null}
          preview={preview ?? undefined}
        />
      )}
    </>
  );
}

function TermLineRow({
  line,
  parked,
  figure,
  canEdit,
  onEdit,
  onDelete,
}: {
  line: TermLine;
  /** True for a clause the chosen period does not cover. Drawn only on request. */
  parked: boolean;
  /** What this clause came to in the chosen period, when it billed at all. */
  figure: DealStatementLine | undefined;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): ReactNode {
  return (
    <tr className={parked ? styles.parkedRow : undefined}>
      <Td strong>
        <span className={styles.lineName}>
          {line.label}
          <Badge shape="tag" tone={line.kind === "revenue" ? "brand" : "teal"}>
            {termLineKindLabel(line.kind)}
          </Badge>
          {!line.active && (
            <Badge shape="tag" tone="neutral">
              Retired
            </Badge>
          )}
        </span>
        {/* THE FIVE CLAUSES AS ONE SENTENCE. The expression is still what is
            stored and still what is edited; this is what makes it checkable
            against the paper by somebody who did not write it. */}
        <span className={styles.sentence}>{savedTermLineSentence(line)}</span>
        {/* A HINT OF THE NOTE, NOT THE NOTE. It is context for whoever edits
            the clause next, and printed in full it was the loudest text on the
            row. The whole of it is on the hover, and in the form that owns
            it. */}
        {line.note !== null && (
          <span className={styles.note} title={line.note}>
            {noteSummary(line.note)}
          </span>
        )}
      </Td>
      <Td muted nowrap>
        {formatCalendarDate(line.effective_from)}
        {line.effective_to === null ? " onward" : ` – ${formatCalendarDate(line.effective_to)}`}
      </Td>
      <Td numeric mono>
        {figure === undefined ? (
          <span className={styles.notBilling}>does not bill</span>
        ) : figure.charged_minor === null ? (
          /* WHY the figure is missing, on the hover. The contract carries the
             reason inside `basis` — the server's own sentence, which opens
             "No figure: …" whenever it refused one — so the client states the
             server's words and invents nothing. */
          <span className={styles.notBilling} title={figure.basis}>
            no figure
          </span>
        ) : (
          formatMoney(figure.charged_minor, line.currency, { omitCurrencyCode: true })
        )}
      </Td>
      {canEdit && (
        <Td>
          <div className={styles.rowActions}>
            <Button small variant="subtle" onClick={onEdit} aria-label={`Edit ${line.label}`}>
              Edit
            </Button>
            <Button small variant="subtle" onClick={onDelete} aria-label={`Delete ${line.label}`}>
              Delete
            </Button>
          </div>
        </Td>
      )}
    </tr>
  );
}

/**
 * Whether the chosen period falls inside this clause's window.
 *
 * A clause is in force in period P when it starts on or before the last day of
 * P and ends on or after the first day of P. Both dates are calendar dates, so
 * the test is on their `YYYY-MM` head and no `Date` is anywhere near it: a
 * month compares as a string exactly as it compares as a month.
 *
 * A RETIRED CLAUSE IS NOT IN FORCE, whatever its window says. Nobody bills it,
 * so it is not a line item of this month or of any other.
 */
export function inForce(line: TermLine, period: string): boolean {
  if (!line.active) return false;
  const from = line.effective_from.slice(0, 7);
  const to = line.effective_to === null ? null : line.effective_to.slice(0, 7);
  return from <= period && (to === null || to >= period);
}

/**
 * The order the clauses read in: revenue first, then the pass-through costs,
 * each oldest window first and ties broken by the label.
 *
 * THIS IS A READING ORDER, NOT THE INVOICE'S LAYOUT ORDER. The invoice has its
 * own, and `StatementPanel` draws that one. What this order is for is a person
 * checking a deal against the paper: what we sell, then what we pass on, in the
 * order the clauses were agreed.
 *
 * The input is left alone. A React render may not sort the array a query holds.
 */
export function readingOrder(lines: readonly TermLine[]): TermLine[] {
  return [...lines].sort((left, right) => {
    if (left.kind !== right.kind) return left.kind === "revenue" ? -1 : 1;
    if (left.effective_from !== right.effective_from) {
      return left.effective_from < right.effective_from ? -1 : 1;
    }
    return left.label.localeCompare(right.label);
  });
}

/**
 * Which month the figures are for.
 *
 * A statement is one period's interpretation, so this is not a filter — it is
 * which statement this IS. Stepping it re-reads the statement and leaves the
 * clauses alone, because a clause is not about a month.
 */
function PeriodStepper({
  period,
  onChange,
}: {
  period: string;
  onChange: (period: string) => void;
}): ReactNode {
  return (
    <span className={styles.stepper}>
      <Button
        small
        variant="secondary"
        aria-label="Previous month"
        onClick={() => onChange(shift(period, -1))}
      >
        ‹
      </Button>
      <span className={styles.stepperLabel}>{formatMonth(period)}</span>
      <Button
        small
        variant="secondary"
        aria-label="Next month"
        onClick={() => onChange(shift(period, 1))}
      >
        ›
      </Button>
    </span>
  );
}

/** `YYYY-MM` plus or minus a month, with no `Date` anywhere near it. */
export function shift(period: string, months: number): string {
  const [yearText = "", monthText = ""] = period.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return period;
  const index = year * 12 + (month - 1) + months;
  const nextYear = Math.floor(index / 12);
  const nextMonth = index - nextYear * 12 + 1;
  return `${String(nextYear).padStart(4, "0")}-${String(nextMonth).padStart(2, "0")}`;
}
